import { format, parse, isValid } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

/** Explicit date-fns tokens. Instants are formatted in a named zone; literal dates stay literal. */
export const validTimeZone = (zone?: string | null): boolean => {
  if (!zone) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(0); return true; } catch { return false; }
};
export const systemTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
/** Date-fns context with UTC field access, avoiding native host DST normalization. */
class UtcFieldsDate extends Date {
  getFullYear() { return this.getUTCFullYear(); } getMonth() { return this.getUTCMonth(); }
  getDate() { return this.getUTCDate(); } getDay() { return this.getUTCDay(); }
  getHours() { return this.getUTCHours(); } getMinutes() { return this.getUTCMinutes(); }
  getSeconds() { return this.getUTCSeconds(); } getMilliseconds() { return this.getUTCMilliseconds(); }
  getTimezoneOffset() { return 0; }
  setFullYear(y: number, m = this.getUTCMonth(), d = this.getUTCDate()) { return this.setUTCFullYear(y,m,d); }
  setMonth(m: number, d = this.getUTCDate()) { return this.setUTCMonth(m,d); }
  setDate(d: number) { return this.setUTCDate(d); }
  setHours(h: number,m = this.getUTCMinutes(),s = this.getUTCSeconds(),ms = this.getUTCMilliseconds()) { return this.setUTCHours(h,m,s,ms); }
  setMinutes(m: number,s = this.getUTCSeconds(),ms = this.getUTCMilliseconds()) { return this.setUTCMinutes(m,s,ms); }
  setSeconds(s: number,ms = this.getUTCMilliseconds()) { return this.setUTCSeconds(s,ms); }
  setMilliseconds(ms: number) { return this.setUTCMilliseconds(ms); }
}
export const formatUtc = (date: Date | string | number, pattern: string): string => format(new UtcFieldsDate(typeof date === 'string' ? new Date(date).getTime() : +date), pattern);
// Bounded formatter cache avoids rebuilding Intl formatters for each calendar slot.
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(zone: string, name?: 'short' | 'long'): Intl.DateTimeFormat {
  const key = zone + '|' + (name || 'fields');
  let formatter = zoneFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', name ? { timeZone: zone, timeZoneName: name } : { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    if (zoneFormatters.size >= 64) zoneFormatters.delete(zoneFormatters.keys().next().value!);
    zoneFormatters.set(key, formatter);
  }
  return formatter;
}
export function formatInstant(date: Date | string | number, pattern: string, zone = systemTimeZone()): string {
  const instant = new Date(typeof date === 'string' ? new Date(date).getTime() : +date);
  if (!Number.isFinite(+instant)) throw new RangeError('Invalid time value');
  const fields = zoneFormatter(zone).formatToParts(instant);
  const number = (name: string) => Number(fields.find(part => part.type === name)?.value);
  const carrier = new Date(0); carrier.setUTCFullYear(number('year'),number('month')-1,number('day')); carrier.setUTCHours(number('hour'),number('minute'),number('second'),instant.getUTCMilliseconds());
  const minutes = Math.round((+carrier-+instant)/60000), sign = minutes < 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(minutes)/60)).padStart(2,'0'), remainder = String(Math.abs(minutes)%60).padStart(2,'0');
  const quote = (text: string) => "'" + text.replace(/'/g,"''") + "'";
  // Preserve quoted literals; resolve offset/name tokens from the original instant.
  const resolved = pattern.replace(/'(?:''|[^'])*'|x{1,5}|X{1,5}|z{1,4}/g, token => {
    if (token.startsWith("'")) return token;
    if (token.startsWith('z')) return quote(zoneFormatter(zone, token.length === 4 ? 'long' : 'short').formatToParts(instant).find(part => part.type === 'timeZoneName')!.value);
    if (token.startsWith('X') && minutes === 0) return "'Z'";
    return quote(sign + hours + (token.length === 1 ? (remainder === '00' ? '' : remainder) : token.length === 2 || token.length === 4 ? remainder : ':' + remainder));
  });
  return formatUtc(carrier, resolved);
}
export function parseCalendarDate(value: string, local = false): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const result = new Date(0);
  if (local) { result.setFullYear(year, month - 1, day); result.setHours(0, 0, 0, 0); }
  else { result.setUTCFullYear(year, month - 1, day); result.setUTCHours(0, 0, 0, 0); }
  return (local ? format(result, 'yyyy-MM-dd') : formatUtc(result, 'yyyy-MM-dd')) === value ? result : null;
}
export function parseLocalDate(value: string, pattern: string): Date | null {
  const result = parse(value, pattern, new Date());
  return isValid(result) && format(result, pattern) === value ? result : null;
}
export const leapYear = (year: number) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
export function addUtc(value: Date, amount: number, unit = 'day'): Date {
  const date = new Date(value);
  const normalized = ({ d: 'day', M: 'month', y: 'year', m: 'minute', h: 'hour', s: 'second', ms: 'millisecond', FullYear: 'year' } as Record<string, string>)[unit] || unit.toLowerCase().replace(/s$/, '');
  if (normalized === 'day' || normalized === 'date' || normalized === 'week') date.setUTCDate(date.getUTCDate() + amount * (normalized === 'week' ? 7 : 1));
  else if (normalized === 'month' || normalized === 'year') {
    const day = date.getUTCDate(); date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + amount * (normalized === 'year' ? 12 : 1));
    const last = new Date(date); last.setUTCMonth(last.getUTCMonth() + 1, 0);
    date.setUTCDate(Math.min(day, last.getUTCDate()));
  } else date.setTime(date.getTime() + amount * ({ hour: 3600000, minute: 60000, second: 1000, millisecond: 1 }[normalized] || 1));
  return date;
}
export function startUtc(value: Date, unit = 'day'): Date {
  const date = new Date(value); const normalized = ({ d: 'day', M: 'month', y: 'year', m: 'minute', h: 'hour', s: 'second', ms: 'millisecond', FullYear: 'year' } as Record<string, string>)[unit] || unit.toLowerCase().replace(/s$/, '');
  if (['year', 'month', 'week', 'day', 'date'].includes(normalized)) {
    date.setUTCHours(0, 0, 0, 0);
    if (normalized === 'year') date.setUTCMonth(0, 1);
    if (normalized === 'month') date.setUTCDate(1);
    if (normalized === 'week') date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  } else if (normalized === 'hour') date.setUTCMinutes(0, 0, 0);
  else if (normalized === 'minute') date.setUTCSeconds(0, 0);
  else if (normalized === 'second') date.setUTCMilliseconds(0);
  return date;
}
export const endUtc = (date: Date, unit = 'day') => new Date(addUtc(startUtc(date, unit), 1, unit).getTime() - 1);
export function zonedDateBoundary(value: string, zone: string): Date {
  if (!parseCalendarDate(value) || !validTimeZone(zone)) throw new Error('Choose a valid date and timezone.');
  const date = fromZonedTime(`${value}T00:00:00`, zone);
  if (formatInstant(date, 'yyyy-MM-dd', zone) !== value) throw new Error('Choose a valid calendar date.');
  return date;
}
